import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '../common';
import { useResolveSegmentConflict } from '../../hooks/useTranslationProjects';
import type { TranslationChapter } from '../../lib/translation/api';

interface Props {
  projectId: string;
  chapters: TranslationChapter[];
  onClose: () => void;
}

// Slide-over that lists every line where your local edit and the synced repo
// text disagree (flagged by the three-way merge on pull). For each one you pick
// which version to keep; resolving clears the conflict via ResolveTranslationSegmentConflict.
export default function ConflictsPanel({ projectId, chapters, onClose }: Props) {
  const { t } = useTranslation();
  const resolve = useResolveSegmentConflict(projectId);

  // Flatten conflicted segments across chapters, keeping chapter context.
  const conflicts = useMemo(
    () =>
      chapters.flatMap((ch) =>
        ch.segments
          .filter((s) => s.conflicted)
          .map((s) => ({ chapterTitle: ch.displayTitle, segment: s }))
      ),
    [chapters]
  );

  const choose = (segmentId: string, choice: 'mine' | 'repo') =>
    resolve.mutate({ segmentId, choice });

  return (
    <div className="fixed inset-0 z-50 flex">
      <button type="button" className="flex-1 bg-black/30" onClick={onClose} aria-label="Close" />
      <div className="flex w-full max-w-md flex-col bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-slate-100 p-4 dark:border-gray-700">
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100">
            {t('translationReader.resolveConflicts')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        {conflicts.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            {t('translationReader.noConflicts')}
          </div>
        ) : (
          <>
            <p className="px-4 pt-3 text-sm text-slate-500 dark:text-slate-400">
              {t('translationReader.conflictsIntro')}
            </p>
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {conflicts.map(({ chapterTitle, segment }) => (
                <div
                  key={segment.id}
                  className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/50 dark:bg-amber-900/10"
                >
                  <div className="mb-2 truncate text-xs font-medium uppercase tracking-wide text-slate-400">
                    {chapterTitle}
                  </div>
                  {segment.sourceText && (
                    <p className="mb-3 text-sm italic leading-relaxed text-slate-400">
                      {segment.sourceText}
                    </p>
                  )}

                  <div className="mb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {t('translationReader.conflictMine')}
                  </div>
                  <p className="mb-3 rounded bg-white p-2 text-sm leading-relaxed text-slate-800 dark:bg-gray-800 dark:text-slate-100">
                    {segment.targetText || <span className="italic text-slate-400">—</span>}
                  </p>

                  <div className="mb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {t('translationReader.conflictRepo')}
                  </div>
                  <p className="mb-3 rounded bg-white p-2 text-sm leading-relaxed text-slate-800 dark:bg-gray-800 dark:text-slate-100">
                    {segment.conflictRepoText || <span className="italic text-slate-400">—</span>}
                  </p>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={resolve.isPending}
                      onClick={() => choose(segment.id, 'repo')}
                    >
                      {t('translationReader.useRepo')}
                    </Button>
                    <Button
                      size="sm"
                      disabled={resolve.isPending}
                      onClick={() => choose(segment.id, 'mine')}
                    >
                      {t('translationReader.keepMine')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {resolve.isPending && (
          <div className="flex items-center justify-center border-t border-slate-100 p-2 text-sm text-slate-400 dark:border-gray-700">
            <Spinner size="sm" /> <span className="ml-2">{t('translationReader.conflictResolving')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
